import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { Observable, of } from 'rxjs';

import { showEditPageItemNameEditorErrors } from '../../../actions/editPageItemNameEditor';
import { updateRateCorrectionsEditorValidation } from '../../../actions/rateCorrectionsEditor';
import { mapStringToCurrencySymbol } from '../../../mappers/common/common';
import { selectEditPageItemNameEditorValue } from '../../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectGroupValidationParams } from '../../../selectors/groupEdit/group/group';
import { ValidationErrorData } from '../../../typings/common';
import { CampaignType } from '../../../typings/domain';
import { CurrencyCode } from '../../../typings/graphql-grid';
import {
    EditGroupData,
    EditGroupDataValue,
    EditInternalGroupDataValue,
    EditPerformanceGroupDataValue,
    EditTextGroupDataValue
} from '../../../typings/groupsEditState';
import { State } from '../../../typings/state';
import { formatNumber } from '../../FormatHelpers/FormatHelpers';
import { GroupsHelpers } from '../../GroupsHelpers/GroupsHelpers';
import { validateRateCorrectionEditor } from '../../RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';

import * as keyset from './ClientGroupEditErrors.i18n';

const i18n = i18nFactory(keyset);

export interface EditGroupValidationParams {
    minGeneralPrice: number;
    maxGeneralPrice: number;
    currency: CurrencyCode;
}

type ClientErrorGroupKeys = EditTextGroupDataValue &
    EditPerformanceGroupDataValue &
    EditInternalGroupDataValue;

export interface ClientErrorsConfigRule<K extends keyof ClientErrorGroupKeys> {
    code: string;
    text: string;
    validate: (value: ClientErrorGroupKeys[K]) => boolean;
    needToValidate?: (
        group: EditGroupDataValue,
        value: ClientErrorGroupKeys[K]
    ) => boolean;
}

interface ClientErrorGroupRules<K extends keyof ClientErrorGroupKeys> {
    rules: Array<ClientErrorsConfigRule<K>>;
}

const getClientErrorsConfig = (
    params: EditGroupValidationParams
): Partial<{ [K in keyof ClientErrorGroupKeys]: ClientErrorGroupRules<K> }> => {
    return {
        feedId: {
            rules: [
                {
                    code: 'required',
                    validate: (value: string): boolean => {
                        return Boolean(value);
                    },
                    needToValidate: (group: EditGroupDataValue): boolean => {
                        return group.campaignType === CampaignType.PERFORMANCE;
                    },
                    text: i18n('feed-required')
                }
            ]
        },
        name: {
            rules: [
                {
                    code: 'required',
                    validate: (value: string): boolean => {
                        return value.trim() !== '';
                    },
                    text: i18n('name-empty')
                }
            ]
        },
        generalRate: {
            rules: [
                {
                    code: 'maxValue',
                    validate: (value: number): boolean => {
                        return value <= params.maxGeneralPrice;
                    },
                    needToValidate: (
                        group: EditGroupDataValue,
                        value: number
                    ): boolean => {
                        return (
                            (group.campaignType === CampaignType.TEXT ||
                                group.campaignType ===
                                    CampaignType.CPM_BANNER) &&
                            value !== undefined
                        );
                    },
                    text: i18n('general-rate-max-price', {
                        maxValue: formatNumber(params.maxGeneralPrice),
                        currency: mapStringToCurrencySymbol(params.currency)
                    })
                },
                {
                    code: 'minValue',
                    validate: (value: number): boolean => {
                        return value >= params.minGeneralPrice;
                    },
                    needToValidate: (
                        group: EditGroupDataValue,
                        value: number
                    ): boolean => {
                        return (
                            (group.campaignType === CampaignType.TEXT ||
                                group.campaignType ===
                                    CampaignType.CPM_BANNER) &&
                            value !== undefined
                        );
                    },
                    text: i18n('general-rate-min-price', {
                        minValue: formatNumber(params.minGeneralPrice),
                        currency: mapStringToCurrencySymbol(params.currency)
                    })
                }
            ]
        }
    };
};

export interface EditGroupFieldErrorsData {
    errors: Array<ValidationErrorData>;
}

export const getGroupFieldValidationErrors = (
    group: EditGroupData,
    fieldName: string,
    params: EditGroupValidationParams
): Array<ValidationErrorData> => {
    const clientValidationConfig = getClientErrorsConfig(params);

    return clientValidationConfig[fieldName].rules.reduce(
        (
            errors: Array<ValidationErrorData>,
            rule: ClientErrorsConfigRule<'name'>
        ) => {
            const fieldValue = group.value[fieldName];

            if (
                (rule.needToValidate === undefined ||
                    rule.needToValidate(group.value, fieldValue)) &&
                rule.validate(fieldValue) === false
            ) {
                errors.push({
                    path: `groups[${group.id}].${fieldName}`,
                    code: rule.code,
                    text: rule.text
                });
            }

            return errors;
        },
        []
    );
};

export const getGroupNameEditorValidationResult = (
    state: State,
    groupId: string
): ValidationErrorData[] => {
    const validationParams = selectGroupValidationParams(state);
    const {
        editGroups: { groups },
        editPageItemNameEditor
    } = state;
    const editorData = selectEditPageItemNameEditorValue(
        editPageItemNameEditor,
        GroupsHelpers.getGroupEditPageItemEditorId(groupId)
    );
    const newGroupData = {
        ...groups[groupId],
        value: {
            ...state.editGroups.groups[groupId].value,
            name: editorData.value
        }
    };

    return getGroupFieldValidationErrors(
        newGroupData,
        'name',
        validationParams
    );
};

export const getGroupsOpenedEditorsValidationResult = (
    groups: Array<EditGroupData>,
    state: State
): { valid: boolean; showErrorsActions: Array<Observable<Action>> } => {
    let isValid = true;
    const showErrorsActions: Array<Observable<Action>> = [];

    groups.forEach((group: EditGroupData) => {
        const groupId = group.id;
        const editorId = GroupsHelpers.getGroupEditPageItemEditorId(groupId);

        const groupNameEditorData = selectEditPageItemNameEditorValue(
            state.editPageItemNameEditor,
            editorId
        );

        if (groupNameEditorData.isVisible) {
            const groupNameEditorErrors = getGroupNameEditorValidationResult(
                state,
                groupId
            );

            if (groupNameEditorErrors.length !== 0) {
                isValid = false;

                showErrorsActions.push(
                    of(
                        showEditPageItemNameEditorErrors(
                            editorId,
                            groupNameEditorErrors
                        )
                    )
                );
            }
        }

        if (
            group.value.campaignType &&
            [
                CampaignType.TEXT,
                CampaignType.CPM_BANNER,
                CampaignType.PERFORMANCE
            ].includes(group.value.campaignType) &&
            state.rateCorrectionsEditor[groupId] &&
            state.rateCorrectionsEditor[groupId].isVisible
        ) {
            const rateCorrectionValidationResult = validateRateCorrectionEditor(
                state.rateCorrectionsEditor[groupId].value
            );

            if (!rateCorrectionValidationResult.isValid) {
                isValid = false;
                showErrorsActions.push(
                    Observable.of(
                        updateRateCorrectionsEditorValidation(
                            groupId,
                            rateCorrectionValidationResult.errors
                        )
                    )
                );
            }
        }
    });

    return {
        valid: isValid,
        showErrorsActions
    };
};
